import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import './index.css'

// Current application version tracker — silent tracking without flickering reloads
const CURRENT_VERSION = 'v1.1.0'
if (localStorage.getItem('vc_app_version') !== CURRENT_VERSION) {
  localStorage.setItem('vc_app_version', CURRENT_VERSION)
  // Limpieza agresiva de cachés de versiones antiguas (< v1.1.0)
  if ('caches' in window) {
    caches.keys().then(keys => {
      keys.forEach(k => {
        if (k !== 'manabi-offline-v110' && k !== 'manabi-api-v1') caches.delete(k)
      })
    })
  }
}

// Centralized frontend error listener: auto-reports uncaught errors to /api/logs/error
if (typeof window !== 'undefined') {
  const isNoise = (msg, stack, fn) => {
    const s = `${msg || ''} ${stack || ''} ${fn || ''}`.toLowerCase()
    return (
      s.includes('ethereum') ||
      s.includes('__firefox__') ||
      s.includes('chrome-extension://') ||
      s.includes('moz-extension://') ||
      s.includes('safari-extension://') ||
      (msg === 'Script error.' && !stack)
    )
  }

  const sendReport = (body) => {
    if (isNoise(body?.message, body?.stack, body?.extra?.filename)) return
    try {
      const token = localStorage.getItem('polar_token')
      const headers = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`
      fetch('/api/logs/error', {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      }).catch(() => {})
    } catch (_) {}
  }

  window.addEventListener('error', (e) => {
    sendReport({
      errorName: e?.error?.name || 'WindowError',
      message: e?.message || 'Script error',
      stack: e?.error?.stack,
      url: window.location.href,
      extra: { filename: e?.filename, lineno: e?.lineno, colno: e?.colno }
    })
  })

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason
    sendReport({
      errorName: reason?.name || 'UnhandledRejection',
      message: reason?.message || String(reason || 'Unhandled Promise Rejection'),
      stack: reason?.stack,
      url: window.location.href
    })
  })
}

import App, { ErrorBoundary, LiveView, AdminPage, DoctorView } from './App.jsx'
import Landing from './Landing.jsx'
import AuthPage from './AuthPage.jsx'
import { ForgotPassword, ResetPassword } from './PasswordReset.jsx'
import DemoGate from './DemoGate.jsx'
import Terminos        from './Terminos.jsx'
import RegistroExito  from './RegistroExito.jsx'
import RegistroFallo  from './RegistroFallo.jsx'
import Backend        from './Backend.jsx'
import Privacidad    from './Privacidad.jsx'
import Overlay       from './Overlay.jsx'

// Modo offline: el service worker guarda la app para que abra sin señal de forma suave y sin recargas forzadas
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        registration.update().catch(() => {})
      })
      .catch(() => { /* sin SW: la app sigue normal */ })
  })
}

// Con sesión va directo al Resumen; sin sesión, a la Landing.
function HomeGate() {
  const hasToken = typeof window !== 'undefined' && Boolean(localStorage.getItem('polar_token'))
  if (hasToken || window.__MANABI_SHELL__ || window.__VITALCORE_SHELL__) {
    return <Navigate to={hasToken ? '/resumen' : '/login'} replace />
  }
  return <Landing />
}

// Rescate de sesión dentro de la app: si el teléfono borró el almacenamiento
// del WebView, el caparazón empuja la sesión guardada (evento 'vc-native',
// kind 'session') después de cargar; acá se adopta y se entra sin pedir login.
// Se escucha SIEMPRE (no solo con __VITALCORE_SHELL__): si la inyección
// temprana del caparazón falló, la marca tampoco existe — y este rescate es
// justamente para ese caso. Fuera de la app el evento no existe y no pasa nada.
function ShellSessionBridge() {
  useEffect(() => {
    const onNative = (e) => {
      const d = e?.detail
      if (d?.kind !== 'session' || !d.token) return
      if (localStorage.getItem('polar_token')) return   // ya hay sesión: no pisar
      try {
        localStorage.setItem('polar_token', d.token)
        localStorage.setItem('polar_user', JSON.stringify(d.user ?? null))
        window.location.replace('/resumen')             // arranque limpio ya logueado
      } catch { /* sin storage: la web sigue pidiendo login */ }
    }
    window.addEventListener('vc-native', onNative)
    return () => window.removeEventListener('vc-native', onNative)
  }, [])
  return null
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <ErrorBoundary>
        <ShellSessionBridge />
        <Routes>
          <Route path="/"          element={<HomeGate />} />
          <Route path="/login"     element={<AuthPage mode="login" />} />
          <Route path="/register"  element={<AuthPage mode="register" />} />
          <Route path="/recuperar"   element={<ForgotPassword />} />
          <Route path="/restablecer" element={<ResetPassword />} />
          <Route path="/live"      element={<LiveView />} />
          <Route path="/doctor/:token" element={<DoctorView />} />
          <Route path="/demo/:token" element={<DemoGate />} />
          <Route path="/terminos"          element={<Terminos />} />
          <Route path="/registro/exito"    element={<RegistroExito />} />
          <Route path="/registro/fallo"    element={<RegistroFallo />} />
          <Route path="/registro/pendiente" element={<RegistroFallo />} />
          <Route path="/backend"   element={<Backend />} />
          <Route path="/admin"     element={<AdminPage />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/overlay/:streamKey" element={<Overlay />} />
          <Route path="/app/*"     element={<App />} />
          {/* backward compat: /monitor still works */}
          <Route path="/*"         element={<App />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  </StrictMode>,
)
